import { useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import {
  ROTULO_CATEGORIA_ICONE,
  glifoDoIcone,
  iconesPorCategoria,
  type IconeBiblioteca,
} from '@miolos/core';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

/**
 * Seletor de ícones da biblioteca.
 *
 * O ícone escolhido entra no miolo como uma camada — igual a uma imagem
 * enviada: você posiciona, gira, muda de tamanho, escolhe em quais páginas
 * aparece e a cor.
 */
export function SeletorDeIcones({
  cor,
  aoEscolher,
  aoFechar,
}: {
  /** Cor usada na prévia, normalmente o destaque da paleta. */
  cor: string;
  aoEscolher: (icone: IconeBiblioteca) => void;
  aoFechar?: () => void;
}) {
  const [busca, setBusca] = useState('');
  const grupos = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return iconesPorCategoria()
      .map(({ categoria, icones }) => ({
        categoria,
        icones: termo
          ? icones.filter((icone) => icone.nome.toLowerCase().includes(termo))
          : icones,
      }))
      .filter((entrada) => entrada.icones.length > 0);
  }, [busca]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            value={busca}
            onChange={(evento) => setBusca(evento.target.value)}
            placeholder="Buscar ícone: coração, lápis, dente..."
            className="h-9 w-full rounded-md border border-border bg-background pl-8 pr-3 text-sm"
          />
        </div>
        {aoFechar ? (
          <Button variant="fantasma" tamanho="icone" onClick={aoFechar} aria-label="Fechar">
            <X />
          </Button>
        ) : null}
      </div>

      {grupos.length === 0 ? (
        <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          Nenhum ícone com esse nome. A busca considera os {iconesPorCategoria().reduce((t, g) => t + g.icones.length, 0)}{' '}
          ícones da biblioteca.
        </p>
      ) : null}

      <div className="max-h-[26rem] space-y-5 overflow-y-auto pr-1">
        {grupos.map(({ categoria, icones }) => (
          <section key={categoria} className="space-y-2">
            <div className="flex items-center gap-2">
              <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                {ROTULO_CATEGORIA_ICONE[categoria]}
              </h4>
              <Badge className="text-[10px]">{icones.length}</Badge>
            </div>

            <div className="grid grid-cols-4 gap-2 sm:grid-cols-6 lg:grid-cols-8">
              {icones.map((icone) => (
                <button
                  key={icone.id}
                  type="button"
                  onClick={() => aoEscolher(icone)}
                  title={icone.nome}
                  className="flex flex-col items-center gap-1 rounded-lg border border-border bg-card p-2 transition-colors hover:border-primary hover:bg-primary/[0.04]"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="26"
                    height="26"
                    fill="none"
                    stroke={cor}
                    strokeWidth={1.7}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                    dangerouslySetInnerHTML={{ __html: glifoDoIcone(icone.id) }}
                  />
                  <span className="w-full truncate text-center text-[9px] leading-tight text-muted-foreground">
                    {icone.nome}
                  </span>
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>

      <p className="rounded-md border border-border bg-muted/50 p-3 text-xs leading-relaxed text-muted-foreground">
        Os {iconesPorCategoria().reduce((t, g) => t + g.icones.length, 0)} ícones desta biblioteca foram desenhados
        para este gerador: você pode usar em miolo que vai vender, sem crédito obrigatório. Ícones de outros sites
        (Flaticon, por exemplo) podem entrar pelo envio de imagem — nesse caso, confira a licença e dê o crédito
        pedido por quem publicou.
      </p>
    </div>
  );
}
